import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { computed, Injectable, signal } from '@angular/core';
import {
  Observable,
  tap,
  catchError,
  BehaviorSubject,
  shareReplay,
  firstValueFrom,
  switchMap,
  of,
  map
} from 'rxjs';
import { END_POINTS, API_URLS } from '../globalConfig/api.config';
import { SHOW_ERROR_TOAST, SUCCESS_TOAST } from './toast-context';
import { AppValidationMessages, ErrorHandlerService, TokenService } from '@invessio/ui';
import { ApplicationConfigurationDto, IsRegisteredUserResponseDto } from '../models/dto/user.dto';
import { ApiResponse } from '../models/dto';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private authPermissions = signal<string[]>([]);
  private authRoles: string[] = [];
  private inviteApplicationCode = signal<string | null>(
    sessionStorage.getItem('transientToken')
  );
  private authLoadedSubject = new BehaviorSubject<boolean>(false);
  private _userDisplayName: string | undefined;
  private applicantRedirectUrl = END_POINTS.AUTHAPPLICANTSELECTIONROUTE;
  private _isAuthenticated = signal<boolean>(false);
  private studentApplicationsCache: any = null;
  private borrowerApplicationRoles = signal<{
    hasApplicant: boolean;
    hasCoBorrower: boolean;
  } | null>(null);

  constructor(
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService,
    private router: Router,
    private tokenService: TokenService
  ) {}

  getStudentApplicationsCache(): any {
    return this.studentApplicationsCache;
  }

  clearStudentApplicationsCache(): void {
    this.studentApplicationsCache = null;
  }

  getBorrowerApplicationRoles(): {
    hasApplicant: boolean;
    hasCoBorrower: boolean;
  } | null {
    return this.borrowerApplicationRoles();
  }

  get permissionsLoaded$() {
    return this.authLoadedSubject.asObservable();
  }

  setIsAuthenticated(value: boolean) {
    this._isAuthenticated.set(value);
    sessionStorage.setItem('isAuthenticated', value.toString());
  }

  readonly isAuthenticated = computed(() => {
    return this._isAuthenticated();
  });

  authenticate(email: string, password: string) {
    const url = `${API_URLS.API_URL}${END_POINTS.AUTHENTICATE}`;
    return this.http
      .post(url, { email, password }, {
        observe: 'response',
        context: new HttpContext().set(SHOW_ERROR_TOAST, true),
      })
      .pipe(
        tap(() => {
          this.setIsAuthenticated(true);
        })
      );
  }

  registerApplicant(password: string) {
    const url = `${API_URLS.API_URL}${END_POINTS.REGISTERAPPLICANT}`;
    return this.http.post<ApiResponse<{ applicationCode: string }>>(url, { password }, {
      observe: 'response',
      context: new HttpContext().set(SHOW_ERROR_TOAST, true),
    });
  }

  registerCoBorrower(password: string) {
    const url = `${API_URLS.API_URL}${END_POINTS.REGISTERCOBORROWER}`;
    return this.http.post<ApiResponse<{ applicationCode: string }>>(url, { password }, {
      observe: 'response',
      context: new HttpContext().set(SHOW_ERROR_TOAST, true),
    });
  }

  isRegisteredUser() {
    const url = `${API_URLS.API_URL}${END_POINTS.REGISTEREDUSER}`;
    return this.http.post<IsRegisteredUserResponseDto>(url, {}, {
      observe: 'response',
      // context: new HttpContext().set(SHOW_ERROR_TOAST, true),
    });
  }

  forgotPassword(email: string) {
    const url = `${API_URLS.API_URL}${END_POINTS.FORGOTPASSWORD}`;
    return this.http.post(url, { email }, {
      observe: 'response',
      context: new HttpContext()
      .set(SUCCESS_TOAST, AppValidationMessages.PASSWORD_RESET_EMAIL_SENT)
      .set(SHOW_ERROR_TOAST, true),
    });
  }

  resetPassword(email: string, newPassword: string, token: string) {
    const url = `${API_URLS.API_URL}${END_POINTS.RESETPASSWORD}`;
    return this.http.post(url, { email, newPassword, token }, {
      observe: 'response',
      context: new HttpContext()
      .set(SHOW_ERROR_TOAST, true),
    });
  }

  validateResetPasswordToken(userId: string, token: string) {
   const url = `${API_URLS.API_URL}${END_POINTS.VALIDATERESETPASSWORDTOKEN}`;
   return this.http.post(url, { userId, token }, {
      context: new HttpContext()
        .set(SHOW_ERROR_TOAST, true),
    }
  );
}

  public async validateOtp(otp: string): Promise<void> {
    const response: HttpResponse<any> = await firstValueFrom(
      this.http.post<any>(`${API_URLS.API_URL}${END_POINTS.OTPVALIDATION(otp)}`, {}, { observe: 'response' })
    );
    const transientAuth = response.headers.get('transient-auth');
    if (transientAuth) {
      sessionStorage.setItem('transient-auth', transientAuth);
    }
  }

  public async resendOtp(): Promise<void> {
    await firstValueFrom(
      this.http.post(`${API_URLS.API_URL}${END_POINTS.OTPRESEND}`, {}, {
        context: new HttpContext()
          .set(SUCCESS_TOAST, AppValidationMessages.OTP_RESEND_SUCCESS)
          .set(SHOW_ERROR_TOAST, true),
      }),
      { defaultValue: null }
    );
  }

  async signOut() {
    const navigated = await this.router.navigate(['/auth']);
    if (navigated) {
      this.tokenService.clearAuthData();
      sessionStorage.clear();
      this.studentApplicationsCache = null;
      this.borrowerApplicationRoles.set(null);
      await firstValueFrom(this.http.post(`${API_URLS.API_URL}${END_POINTS.SIGNOUT}`, {}, { responseType: 'text' }));
      this.setIsAuthenticated(false);
    }
  }

  static isOperationsAdmin = false;
  setAuth(roles: string[], perms: string[]): void {
    this.authPermissions.set(perms ?? []);
    this.authRoles = roles;
    PermissionService.isOperationsAdmin =
      perms.includes('update_application_status') &&
      perms.includes('view_operations_application_list');
    this.authLoadedSubject.next(true);
  }


  public hasRole(roleName: string): boolean {
    return this.authRoles.includes(roleName);
  }

  hasAuthPermissions = (perm: string) =>
    computed(() => this.authPermissions().includes(perm));

  getAll(): string[] {
    return [...this.authPermissions()];
  }

  getInviteApplicationCode(): string | null {
    return this.inviteApplicationCode();
  }

  getDisplayName(): string | undefined {
    return this._userDisplayName;
  }

  getApplicantRedirectUrl(): string {
    return this.applicantRedirectUrl;
  }

  getDefaultPostAuthUrl(): string {
    const isOperationsAdmin = this.hasAuthPermissions('update_application_status') && this.hasAuthPermissions('view_operations_application_list');
    const isAdmin = isOperationsAdmin || this.hasAuthPermissions('view_school_application_list');
    if (this.hasRole('Applicant')) {
      return this.getApplicantRedirectUrl();
    }

    if (isOperationsAdmin) {
      return END_POINTS.AUTHOPERATIONSADMINROUTE;
    }

    if (isAdmin) {
      return END_POINTS.AUTHSCHOOLADMINROUTE;
    }

    return END_POINTS.GETINVITATION;
  }

  fetchAuth(isCommingFromCoborrower = false): Observable<ApplicationConfigurationDto> {
    this.studentApplicationsCache = null;
    this.borrowerApplicationRoles.set(null);
    this.authPermissions.set([]);
    this.authRoles = [];
    this.authLoadedSubject.next(false);
    return this.http
      .get<ApplicationConfigurationDto>(API_URLS.APPLICATION_CONFIGURATION)
      .pipe(
        switchMap((config) => {
          const perms = config?.user?.permissions ?? [];
          const roles = config?.user?.roles ?? [];
          this._userDisplayName = config?.user?.name;
          this.inviteApplicationCode.set(config?.user?.applicationCode);
          this.setAuth(roles, perms);
          this.setIsAuthenticated(!!config?.isAuthenticated);

          this.applicantRedirectUrl = END_POINTS.AUTHAPPLICANTSELECTIONROUTE;

          if (config?.isAuthenticated && roles.includes('Applicant')) {
            const url = `${API_URLS.API_URL}${END_POINTS.GET_ALL_APPLICATION_FOR_BORROWER}`;
            return this.http.get<any>(url).pipe(
              map((res) => {
                this.studentApplicationsCache = res;
                const applicants = res?.data?.applicantsList || [];
                const coBorrowers = res?.data?.coBorrowerList || [];
                this.borrowerApplicationRoles.set({
                  hasApplicant: applicants.length > 0,
                  hasCoBorrower: coBorrowers.length > 0,
                });
                const apps = [...applicants, ...coBorrowers];
                if (apps.length === 1 && !apps[0].inviteUrl && apps[0].applicationCode) {
                  this.applicantRedirectUrl = END_POINTS.AUTHSTUDENTROUTE(apps[0].applicationCode);
                }
                else if(apps.length > 1 && isCommingFromCoborrower)
                {
                  const currentApplicants = apps.filter(
                    applicant => !!applicant.applicationCode && applicant.inviteUrl == ''
                  );
                  if(currentApplicants.length === 1 && !currentApplicants[0].inviteUrl && currentApplicants[0].applicationCode)
                  {
                    this.inviteApplicationCode.set(currentApplicants[0].applicationCode);
                    this.applicantRedirectUrl = END_POINTS.AUTHSTUDENTROUTE(currentApplicants[0].applicationCode);
                  }
                }
                return config;
              }),
              catchError(() => of(config))
            );
          }

          return of(config);
        }),
        catchError((err) => {
          this.errorHandlerService.handleError(err);
          throw err;
        }),
        shareReplay(1)
      );
  }
}
